import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useQuery } from '@tanstack/react-query';
import { PAGE_CONFIGS } from 'queries/query-keys';
import { maybeInjectPermanentItems } from 'pages/Account/pages/Toolbar/hooks/useRightColumn';
import useToolbarAccessFiltering from 'pages/Account/pages/Toolbar/hooks/useToolbarAccessFiltering';
import TeamMemberService from 'services/TeamMemberService';
import { Redirect } from 'react-router-dom';
import { getFirstInternalNavItem } from 'app/index';
import { ErrorMessage } from 'components/ErrorMessage';
import { PluginContext } from 'ts-components/Plugins/context';
import { isNonLandingPagePath } from 'routes/paths';
import {
  DashboardErrorCodeText,
  DashboardWarningText,
} from 'pages/Dashboard/styles';

// Avoid circle redirect if user doesn't have any Role
const getRedirectPath = (path) =>
  !path || isNonLandingPagePath(path) ? '/dashboard' : path;

export default () => {
  const { t } = useTranslation();
  const teamMember = useSelector((s) => s.authentication.teamMember);
  const business = useSelector((s) => s.authentication.chosenBusiness);
  const access = useSelector((s) => s.authentication.access);
  const anyAccess = Object.values(access.sections).some(
    (section) => section.view
  );

  const toolbarEnabled = Boolean(teamMember?.id) && Boolean(business?.id);

  const filterToolbar = useToolbarAccessFiltering();

  const { data: unfilteredToolbarData, isLoading: toolbarLoading } = useQuery({
    queryKey: PAGE_CONFIGS.MY_TOOLBAR({
      user: teamMember?.id,
      business: business?.id,
    }),
    queryFn: async () => {
      const res = await TeamMemberService.getPageConfig('toolbar');
      return {
        ...res,
        elements: res?.elements ?? [],
      };
    },
    enabled: toolbarEnabled,
  });

  const { isLoading: pluginsLoading, toolbarItemsByFullId } =
    useContext(PluginContext);

  // The filtering has to happen outside of the query function, otherwise the
  // plugin-less list computed while plugin configs are loading gets frozen into
  // the query cache and plugin toolbar items can never become eligible
  const toolbarElements = useMemo(() => {
    if (!toolbarEnabled || toolbarLoading) {
      return undefined;
    }

    return filterToolbar(
      maybeInjectPermanentItems(
        unfilteredToolbarData?.elements ?? [],
        unfilteredToolbarData?.modified
      )
    );
  }, [filterToolbar, unfilteredToolbarData, toolbarEnabled, toolbarLoading]);

  const firstNavItem = getFirstInternalNavItem(
    toolbarElements,
    toolbarItemsByFullId
  );
  const redirectPath = getRedirectPath(firstNavItem);

  const canRedirect =
    anyAccess &&
    (redirectPath !== '/dashboard' ||
      (redirectPath === '/dashboard' &&
        access?.sections.dashboards_section?.view === true));

  return toolbarLoading || pluginsLoading ? null : canRedirect ? (
    <Redirect to={redirectPath} />
  ) : (
    <ErrorMessage
      title={t('Permissions Needed')}
      text={
        <DashboardWarningText as="p">
          {t(
            'The page you are trying to view requires special permissions or is no longer active. If you believe you should have permissions to view, please contact your Business Administrator.'
          )}
        </DashboardWarningText>
      }
      code={
        <DashboardErrorCodeText as="p">
          {t('Error code')}: 403
        </DashboardErrorCodeText>
      }
      dashboard
    />
  );
};
